import React, { useState } from 'react';
import { X } from 'lucide-react';
import { ImageUpload } from '../../common/ImageUpload';
import { useJobs } from '../../../hooks/admin/useJobs';
import { toast } from 'react-hot-toast';
import { TECHNICAL_SKILLS, SOFT_SKILLS } from '../../../data/availableSkills';
import type { Job } from '../../../types/job';

interface JobFormProps {
  initialData?: Job;
  onClose: () => void;
  onSuccess: () => void;
}

function JobForm({ initialData, onClose, onSuccess }: JobFormProps) {
  const [formData, setFormData] = useState({
    title: initialData?.title || '',
    company: initialData?.company || '',
    location: initialData?.location || '',
    type: initialData?.type || 'Full-time',
    salary: initialData?.salary || '',
    logo: initialData?.logo || '',
    description: initialData?.description || '',
    technicalSkills: initialData?.technicalSkills || [],
    softSkills: initialData?.softSkills || []
  });

  const { createJob, updateJob, isLoading } = useJobs();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title || !formData.company || !formData.location || !formData.salary) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      if (initialData) {
        await updateJob(initialData.id, formData);
      } else {
        await createJob(formData);
      }
      toast.success(initialData ? 'Job updated successfully' : 'Job created successfully');
      onSuccess();
    } catch (error) {
      console.error('Error saving job:', error);
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error('Failed to save job');
      }
    }
  };

  const handleTechnicalSkillsChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedSkills = Array.from(e.target.selectedOptions, option => option.value);
    setFormData(prev => ({ ...prev, technicalSkills: selectedSkills }));
  };

  const handleSoftSkillsChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedSkills = Array.from(e.target.selectedOptions, option => option.value);
    setFormData(prev => ({ ...prev, softSkills: selectedSkills }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-background-card rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-800">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-black">
              {initialData ? 'Edit Job' : 'Add New Job'}
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-300">
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Company Logo
            </label>
            <ImageUpload
              onUpload={(url) => setFormData(prev => ({ ...prev, logo: url }))}
              currentImageUrl={formData.logo}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-black mb-2">
                Job Title
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))}
                className="input w-full text-black"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-black mb-2">
                Company
              </label>
              <input
                type="text"
                value={formData.company}
                onChange={e => setFormData(prev => ({ ...prev, company: e.target.value }))}
                className="input w-full text-black"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-black mb-2">
                Location
              </label>
              <input
                type="text"
                value={formData.location}
                onChange={e => setFormData(prev => ({ ...prev, location: e.target.value }))}
                className="input w-full text-black"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-black mb-2">
                Type
              </label>
              <select
                value={formData.type}
                onChange={e => setFormData(prev => ({ ...prev, type: e.target.value }))}
                className="input w-full text-black"
              >
                <option>Full-time</option>
                <option>Part-time</option>
                <option>Contract</option>
                <option>Remote</option>
                <option>Internship</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-black mb-2">
                Salary Range
              </label>
              <input
                type="text"
                value={formData.salary}
                onChange={e => setFormData(prev => ({ ...prev, salary: e.target.value }))}
                className="input w-full text-black"
                placeholder="e.g. $80k - $120k"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-black mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
              className="input w-full h-32 text-black"
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-black mb-2">
                Technical Skills
              </label>
              <select
                multiple
                value={formData.technicalSkills}
                onChange={handleTechnicalSkillsChange}
                className="input w-full h-32 text-black"
              >
                {TECHNICAL_SKILLS.map(skill => (
                  <option key={skill} value={skill}>{skill}</option>
                ))}
              </select>
              <p className="mt-1 text-xs text-black">Hold Ctrl/Cmd to select multiple skills</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-black mb-2">
                Soft Skills
              </label>
              <select
                multiple
                value={formData.softSkills}
                onChange={handleSoftSkillsChange}
                className="input w-full h-32 text-black"
              >
                {SOFT_SKILLS.map(skill => (
                  <option key={skill} value={skill}>{skill}</option>
                ))}
              </select>
              <p className="mt-1 text-xs text-black">Hold Ctrl/Cmd to select multiple skills</p>
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-400 hover:text-white"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary"
            >
              {isLoading ? 'Saving...' : initialData ? 'Update Job' : 'Create Job'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


export { JobForm }